import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import "./ReviewPostView.scss";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import ReviewPostConfiguration from "./ReviewPostConfiguration";
import Loading from "../../../../loading/Loading";
import LoadingIcon from "../../../../../components/icons/LoadingIcon";
import { clientRoutes } from "../../../../../utils/routes";
import { CommonEnums } from "../../../../../enums/common.enums";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import PreviewModal from "../../propertypost/view/PreviewModal";
import ReviewviewMenuIcon from "./ReviewListMenuIcon";
import { PropertyApi } from "../../../../../utils/api/Property";
import { DatePicker } from "@mui/x-date-pickers";
import PostAssets from "./PostAssets";

interface InputField {
  handler: string;
  label: string;
  value: string;
  template_input_index: number;
  characters_limit?: number;
}

interface ReviewPostData {
  _id: string;
  review_type: string;
  review_on: string;
  post_status: string;
  post_type: string;
  agency_ref: string;
  agent_ref: string;
  schedule_datetime: string | null;
  createdAt: string;
  updatedAt: string;
  inputs: Record<string, InputField>;
  design_template_id: string;
  listing_type?: string;
  vendorType?: string;
}

interface FormValues {
  review_type: string;
  post_type: string;
  design_template_id: string;
  schedule_date: dayjs.Dayjs;
  schedule_time: dayjs.Dayjs;
  post_status: string;
  review_on: string;
  inputs: Record<string, InputField>;
}

const reviewTitleMap: Record<string, string> = {
  vendor: "Vendor Review",
  buyer: "Buyer Review",
  landlord: "Landlord Review",
  tenant: "Tenant Review",
};

const reviewOnLabels: Record<string, string> = {
  buyer: "Buyer ",
  vendor: "Vendor (Seller)",
  landlord: "Landlord",
  tenant: "Tenant",
};

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const ReviewPostView = () => {
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(false);
  const [reviewPost, setReviewPost] = useState<ReviewPostData | null>(null);
  const [reviewViewPost, setReviewViewPost] = useState<any>(null);
  const navigate = useNavigate();
  const { reviewPostId } = useParams();
  const [templateData, setTemplateData] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const subscriptionType = searchParams.get("subscription_type") || "";
  const postTypeFromUrl =
    searchParams.get("post_type") || reviewPost?.post_type || "";
  const [localPostStatus, setLocalPostStatus] = useState<string | null>(null);
  const [timeTo, setTimeTo] = useState("00:00");
  const [showScheduleForm, setShowScheduleForm] = useState(true);
  const [date, setDate] = useState<string>("");
  const [selectedButton, setSelectedButton] = useState("schedule");
  const listingType =
    searchParams.get("listing_type") || reviewPost?.listing_type || "";

  const [state, setState] = useState({
    isLoadingSelect: false,
    designTemplates: [],
    isLoading: false,
  });
  const [formInitialValues, setFormInitialValues] = useState<FormValues | null>(
    null
  );

  const [reviewPreviewData, setReviewPreviewData] = useState({
    id: "",
    title: "",
    src: "",
  });

  useEffect(() => {
    if (reviewPost && templateData) {
      const mappedReviewType =
        reviewTitleMap[reviewPost.review_type?.toLowerCase()] ||
        reviewPost.review_type ||
        "";

      setFormInitialValues({
        review_type: mappedReviewType,
        post_type: reviewPost.post_type || "",
        design_template_id: reviewPost.design_template_id || "",
        schedule_date: reviewPost.schedule_datetime
          ? dayjs(new Date(reviewPost.schedule_datetime))
          : dayjs(),
        schedule_time: reviewPost.schedule_datetime
          ? dayjs(new Date(reviewPost.schedule_datetime))
          : dayjs(),
        post_status: reviewPost.post_status || "",
        review_on: reviewPost.review_on || "",
        inputs: inputs,
      });
    }
  }, [reviewPost, templateData, inputs]);

  const onSubmitHandler = async (values: any) => {
    setProcessingModalOpen(true);

    try {
      const payload = {
        review_type: values.review_type,
        post_type: values.post_type,
        schedule_date: values.schedule_date?.format("YYYY-MM-DD"),
        schedule_time: values.schedule_time?.format("HH:mm"),
        post_status: values.post_status,
        review_on: values.review_on,
        inputs: values.inputs,
        finalize: true,
      };

      const res = await PropertyApi.updateReviewPost(
        payload,
        reviewPostId as string
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Internal server error!");
        setProcessingModalOpen(false);
        return;
      }

      setReviewPost((prev: any) =>
        prev ? { ...prev, inputs: values.inputs } : prev
      );

      setProcessingModalOpen(false);
      setIsGenerating(true);

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetReviewPostById(
            reviewPostId as string
          );
          const reviewData = check?.data?.data;

          if (!reviewData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const refs = [
            reviewData?.image_post_design_activity_ref,
            reviewData?.story_post_design_activity_ref,
            reviewData?.video_post_design_activity_ref,
            reviewData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref: any) => ref?.design_task_id?.output_image_path
          );

          if (!firstWithImage) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: firstWithImage.design_task_id.output_image_path,
          });

          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));

          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };

      pollDesignStatus();
    } catch (error) {
      toast.error("Failed to update review post!");
      setProcessingModalOpen(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: formInitialValues || {
      review_type: "",
      post_type: "",
      design_template_id: "",
      schedule_date: dayjs(),
      schedule_time: dayjs(),
      post_status: "",
      review_on: "",
      inputs: {},
    },
    enableReinitialize: true,
    onSubmit: onSubmitHandler,
  });

  // const getDesignTemplatesApi = async () => {
  //   setState({
  //     ...state,
  //     isLoadingSelect: true,
  //   });

  //   try {
  //     let res = await TemplatesApi.getDesignTemplateById(
  //       "688397c5a59d2860a7bcbf5c"
  //     );

  //     if (res?.data?.success) {
  //       setState({
  //         ...state,
  //         isLoadingSelect: false,
  //         designTemplates: res.data.data,
  //       });
  //     } else {
  //       toast.error(res?.data?.message || "Failed to load design templates!");
  //     }
  //   } catch (error) {
  //     toast.error("Failed to load design templates!");
  //     setState({
  //       ...state,
  //       isLoadingSelect: false,
  //     });
  //   }
  // };

  function resolveReviewTemplateType(postType?: string, listingType?: string) {
    const normalizedPostType = (postType || "").toLowerCase();
    const normalizedListingType = (listingType || "").toLowerCase();

    if (
      normalizedPostType === "image_post" &&
      normalizedListingType === "sale"
    ) {
      return "review_for_sale_image_post";
    }
    if (
      normalizedPostType === "story_post" &&
      normalizedListingType === "sale"
    ) {
      return "review_for_sale_story_post";
    }
    if (
      normalizedPostType === "image_post" &&
      normalizedListingType === "rent"
    ) {
      return "review_for_rent_image_post";
    }
    if (
      normalizedPostType === "story_post" &&
      normalizedListingType === "rent"
    ) {
      return "review_for_rent_story_post";
    }

    return undefined;
  }

  const getSelectedTemplateId = (
    postType: string | undefined,
    listingType: string | undefined,
    reviewPost?: any
  ) => {
    const normalizedPostType = (postType || "").toLowerCase();
    const normalizedListingType = (listingType || "").toLowerCase();

    switch (normalizedPostType) {
      case "image_post":
        return (
          reviewPost?.image_post_design_activity_ref?.design_template_id?._id ||
          reviewPost?.design_template_id?._id ||
          null
        );

      case "video_post":
        return (
          reviewPost?.video_post_design_activity_ref?.design_template_id?._id ||
          reviewPost?.design_template_id?._id ||
          null
        );

      case "reel_post":
        return (
          reviewPost?.reel_post_design_activity_ref?.design_template_id?._id ||
          reviewPost?.design_template_id?._id ||
          null
        );

      case "story_post":
        return (
          reviewPost?.story_post_design_activity_ref?.design_template_id?._id ||
          reviewPost?.design_template_id?._id ||
          null
        );

      default:
        return reviewPost?.design_template_id?._id || null;
    }
  };

  const fetchReviewTemplateId = async (
    subscriptionType: string,
    listingType: string,
    postType: string
  ) => {
    try {
      const response = await PropertyApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
      });

      if (!response?.success || !response.data?.data) {
        toast.error("Failed to load template configs!");
        return null;
      }

      const postTypeBlock = response.data.data[postType];
      if (!postTypeBlock || !postTypeBlock.templates) {
        console.warn(`No templates found for postType=${postType}`);
        return null;
      }

      const expectedSlug = resolveReviewTemplateType(postType, listingType);

      if (!expectedSlug) {
        console.warn(`Unsupported combination: ${listingType}, ${postType}`);
        return null;
      }

      const foundTemplate = postTypeBlock.templates.find(
        (tpl: any) =>
          tpl.slug === expectedSlug && tpl.config?.design_template?._id
      );

      if (!foundTemplate) {
        console.warn(`No matching template for slug=${expectedSlug}`);
        return null;
      }

      return foundTemplate.config.design_template._id;
    } catch (err) {
      console.error(err);
      toast.error("Error fetching template configs!");
      return null;
    }
  };

  const fetchTemplate = async (
    forceDesignTemplateId?: string,
    signal?: AbortSignal
  ) => {
    setIsLoading(true);
    try {
      let existingGeneratedImage: string | null = null;
      let reviewData: any = null;
      let designTemplateId: string | null = forceDesignTemplateId || null;

      if (reviewPostId) {
        const reviewRes = await PropertyApi.GetReviewPostById(reviewPostId);

        if (signal?.aborted) return;

        reviewData = reviewRes?.data?.data;

        setReviewViewPost(reviewData);

        if (reviewData) {
          setReviewPost(reviewData);

          const params = new URLSearchParams(searchParams);

          if (!params.get("post_type") && reviewData.post_type) {
            params.set("post_type", reviewData.post_type);
          }
          // if (!params.get("listing_type") && reviewData.listing_type) {
          //   params.set("listing_type", reviewData.listing_type);
          // }

          setSearchParams(params);

          const refs = [
            reviewData?.image_post_design_activity_ref,
            reviewData?.story_post_design_activity_ref,
            reviewData?.video_post_design_activity_ref,
            reviewData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (firstWithImage) {
            existingGeneratedImage = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
          }

          const postTypeFromUrl = searchParams.get("post_type") || "";
          designTemplateId =
            designTemplateId ||
            reviewData?.design_template_id?._id ||
            getSelectedTemplateId(postTypeFromUrl, listingType, reviewData);
        }
      }

      const postTypeFromUrl = searchParams.get("post_type") || "";

      if (!designTemplateId) {
        designTemplateId = await fetchReviewTemplateId(
          subscriptionType,
          listingType,
          postTypeFromUrl
        );
      }

      if (signal?.aborted) return;

      if (!designTemplateId) {
        toast.error("No matching review template found!");
        return;
      }

      const res = await PropertyApi.getDesignTemplateById(designTemplateId);
      if (signal?.aborted) return;

      if (res.success && res.data) {
        const templateFallbackImage =
          res.data?.data?.template_image_ref?.original_file_path ||
          res.data?.data?.template_image;

        setTemplateData({
          ...res.data,
          generatedImage:
            existingGeneratedImage ||
            (templateFallbackImage
              ? `${API_BASE_URL}/${templateFallbackImage}`
              : null),
        });

        const apiInputs = res.data.data?.inputs || {};
        const normalizedInputs: Record<string, InputField> = {};
        let index = 0;

        Object.keys(apiInputs).forEach((key) => {
          const tpl = apiInputs[key];
          const charLimit = tpl?.characters_limit || 100;

          normalizedInputs[key] = {
            value: reviewData?.inputs?.[key]?.value ?? tpl?.value ?? "",
            handler: tpl?.handler || key,
            label: tpl?.label || key,
            template_input_index: tpl?.template_input_index ?? index,
            characters_limit: charLimit,
          };
          index++;
        });

        Object.keys(reviewData?.inputs || {}).forEach((key) => {
          if (!normalizedInputs[key]) {
            normalizedInputs[key] = reviewData.inputs[key];
          }
        });

        setInputs(normalizedInputs);

        formik.setValues((prev) => ({
          ...prev,
          design_template_id: designTemplateId!,
          inputs: normalizedInputs,
          review_on: prev.review_on || reviewData?.review_on || "",
        }));

        return { inputs: normalizedInputs, image: templateFallbackImage };
      } else {
        toast.error(res.error || "Failed to fetch template!");
      }
    } catch (err) {
      if (signal?.aborted) {
        console.log("Fetch aborted");
        return;
      }
      console.error(err);
      toast.error("Failed to fetch template!");
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (reviewPostId) {
      fetchTemplate(undefined, signal);
    }
    return () => {
      controller.abort();
    };
  }, [reviewPostId, subscriptionType, listingType]);

  useEffect(() => {
    const step = searchParams.get("step");

    if (step === "configure_post") {
      setActiveTab("postConfig");
    } else if (step === "post_assets") {
      setActiveTab("post_assets");
    } else {
      setActiveTab("info");
    }
  }, [searchParams]);

  const onChangeDate = (e: any) => {
    formik.setFieldValue("schedule_date", e);
  };

  const onChangeTime = (e: any) => {
    formik.setFieldValue("schedule_time", e);
  };

  const handleInputChange = (key: string, value: string) => {
    formik.setFieldValue(`inputs.${key}.value`, value);
  };

  const resetInputs = () => {
    if (reviewPost) {
      const resetInputs = { ...reviewPost.inputs };
      Object.keys(resetInputs).forEach((key) => {
        resetInputs[key].value = reviewPost.inputs[key].value;
      });

      formik.setFieldValue("inputs", resetInputs);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPreviewContainerClass = (postType: string | null) => {
    switch (postType) {
      case CommonEnums.postType.story_post.key:
        return "story-post-preview";
      case CommonEnums.postType.video_post.key:
        return "video-post-preview";
      case CommonEnums.postType.reel_post.key:
        return "reel-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const formatPostType = (type: string) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const sortedInputs = formik.values.inputs
    ? Object.entries(formik.values.inputs).sort(
        ([, a], [, b]) =>
          (a?.template_input_index ?? 0) - (b?.template_input_index ?? 0)
      )
    : [];

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "Completed";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      case "pending_for_social_media_upload":
        return "Pending Upload";
      case "scheduled for post":
        return "Scheduled";
      case "cancelled":
        return "Cancelled";
      case "errored":
      case "error":
        return "Error";
      default:
        return "N/A";
    }
  };

  const handleUpdatedesign = async () => {
    const reviewTitle =
      formik.values.inputs?.review_post_title?.value?.trim() ||
      formik.values.review_type?.trim();

    if (!reviewTitle) {
      toast.error("Review Post Title is required!");
      return;
    }

    if (!reviewPostId) {
      toast.error("Review Post ID is missing!");
      return;
    }

    const inputsPayload: any = {};
    Object.keys(formik.values.inputs || {}).forEach((key) => {
      if (key !== "review_on") {
        const val = formik.values.inputs[key];
        if (val?.value) {
          inputsPayload[key] = {
            handler: val.handler || key,
            label: val.label || key,
            value: val.value,
            template_input_index: val.template_input_index ?? 0,
            characters_limit: val.characters_limit ?? 0,
          };
        }
      }
    });

    if (!inputsPayload.review_post_title) {
      inputsPayload.review_post_title = {
        handler: "review_post_title",
        label: "Review Post Title",
        value: reviewTitle,
        template_input_index: 0,
        characters_limit: 100,
      };
    }

    const postTypeFromUrl = searchParams.get("post_type") || "";

    const designTemplateId = await fetchReviewTemplateId(
      subscriptionType,
      listingType,
      postTypeFromUrl
    );

    if (!designTemplateId) {
      toast.error("No matching review template found!");
      return;
    }

    const payload = {
      design_template_id: designTemplateId,
      review_on: formik.values.review_on || "",
      inputs: inputsPayload,
      finalize: true,
    };

    setProcessingModalOpen(true);

    try {
      const res = await PropertyApi.updateReviewPost(payload, reviewPostId);

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Internal server error!");
        setProcessingModalOpen(false);
        return;
      }

      setProcessingModalOpen(false);
      setIsGenerating(true);

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetReviewPostById(reviewPostId);
          const reviewData = check?.data?.data;

          if (!reviewData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const refs = [
            reviewData?.image_post_design_activity_ref,
            reviewData?.story_post_design_activity_ref,
            reviewData?.video_post_design_activity_ref,
            reviewData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (!firstWithImage) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: firstWithImage.design_task_id.output_image_path,
          });

          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));

          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };

      pollDesignStatus();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
      setProcessingModalOpen(false);
    }
  };

  const previewImages: string[] = [
    templateData?.generatedImage ||
      (templateData?.data?.template_image_ref?.original_file_path
        ? `${API_BASE_URL}/${templateData.data.template_image_ref.original_file_path}`
        : templateData?.data?.template_image
        ? `${API_BASE_URL}/${templateData.data.template_image}`
        : "/images/image-video-generation-error-fallback-image-for-card.png"),
  ];

  const hasInputChanges = React.useMemo(() => {
    if (!reviewPost?.inputs || Object.keys(reviewPost.inputs).length === 0) {
      return true;
    }

    if (!formik.values?.inputs) return false;

    return Object.keys(formik.values.inputs).some((key) => {
      const originalValue = reviewPost.inputs[key]?.value || "";
      const currentValue = formik.values.inputs[key]?.value || "";
      return originalValue !== currentValue;
    });
  }, [reviewPost?.inputs, formik.values.inputs]);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() => {
                const subscriptionType = searchParams.get("subscription_type");
                const backUrl = subscriptionType
                  ? `${clientRoutes.ReviewPostList}?subscription_type=${subscriptionType}`
                  : clientRoutes.ReviewPostList;

                navigate(backUrl);
              }}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
          </div>

          <div className="header-row">
            <div className="page-title">Review Post</div>
            <div className="action-menu-icon-wrapper">
              {reviewPost?._id && (
                <ReviewviewMenuIcon
                  postId={reviewPost._id}
                  onUpdated={() => fetchTemplate()}
                  // onViewDetails={() =>
                  //   navigate(
                  //     `/workspace/review-post-list/view/${item._id}?subscription_type=${subscriptionType}`
                  //   )
                  // }
                  onPreview={() => setPreviewOpen(true)}
                  previewImageUrl={previewImages[0]}
                />
              )}
            </div>
          </div>

          {loading || !reviewPost ? (
            <div className="list-page-loading">
              <Loading />
            </div>
          ) : (
            <>
              <div className="auction-top-info">
                <div className="top-row">
                  <div className="info-item">
                    <label>Review Post Id</label>
                    <p>{reviewPost._id}</p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p
                      className={`status-badge ${reviewPost.post_status.toLowerCase()}`}
                    >
                      {getStatusBadge(reviewPost.post_status)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Agency</label>
                    <p className="agent-name">
                      {reviewViewPost?.agency_ref?.agency_trading_name}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Agent</label>
                    <p className="agent-name">
                      {reviewViewPost?.agent_ref?.principal_agent_first_name}{" "}
                      {reviewViewPost?.agent_ref?.principal_agent_last_name}
                    </p>
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="info-item">
                    <label>Post Type</label>
                    <p>{formatPostType(reviewPost.post_type)}</p>
                  </div>
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {reviewPost.schedule_datetime
                        ? formatDate(reviewPost.schedule_datetime)
                        : "Not scheduled"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Review Type</label>
                    <p>
                      {" "}
                      {reviewOnLabels[reviewViewPost.review_type] ||
                        reviewViewPost.review_type}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Created On</label>
                    <p>{formatDate(reviewPost.createdAt)}</p>
                  </div>
                </div>
                <div className="bottom-row">
                  <div className="info-item">
                    <label>Last Updated On</label>
                    <p>{formatDate(reviewPost.updatedAt)}</p>
                  </div>
                </div>
              </div>

              <div className="custom-tabs">
                <div
                  className={`tab-item ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("info");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "info",
                    });
                  }}
                >
                  Info
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "post_assets" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("post_assets");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "post_assets",
                    });
                  }}
                >
                  Post Assets
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "postConfig" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("postConfig");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "configure_post",
                    });
                  }}
                >
                  Post Configuration
                </div>
              </div>

              {activeTab === "info" && (
                <>
                  {isLoading ? (
                    <div className="list-page-loading">
                      <Loading />
                    </div>
                  ) : (
                    <form onSubmit={formik.handleSubmit}>
                      <div className="template-inputs-grid">
                        <div className="template-input-fields">
                          <div className="section-heading">Inputs</div>
                          <div className="input-description">
                            Feel free to update the inputs to customize the
                            review post
                          </div>

                          {formik.values?.inputs &&
                          formik.values.inputs.review_post_title ? (
                            <div className="input-field-block">
                              <label className="input-label">
                                {formik.values.inputs.review_post_title.label}*
                                (
                                {formik.values.inputs.review_post_title.value
                                  ?.length || 0}
                                /
                                {formik.values.inputs.review_post_title
                                  .characters_limit || 100}
                                )
                              </label>
                              <textarea
                                className="input-textarea"
                                placeholder={`${formik.values.inputs.review_post_title.label}...`}
                                maxLength={
                                  formik.values.inputs.review_post_title
                                    .characters_limit || 100
                                }
                                name="inputs.review_post_title.value"
                                value={
                                  formik.values.inputs.review_post_title
                                    .value || ""
                                }
                                ref={(el) => autoResize(el)}
                                onInput={(e) => {
                                  e.currentTarget.style.height = "auto";
                                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                }}
                                onChange={(e) =>
                                  handleInputChange(
                                    "review_post_title",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <div className="input-field-block">
                              <label className="input-label">
                                Review Post Title* (
                                {formik.values.review_type?.length || 0}/50)
                              </label>
                              <textarea
                                className="input-textarea"
                                placeholder="Review Post Title..."
                                maxLength={50}
                                name="review_type"
                                value={formik.values.review_type || ""}
                                ref={(el) => autoResize(el)}
                                onInput={(e) => {
                                  e.currentTarget.style.height = "auto";
                                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                }}
                                onChange={formik.handleChange}
                              />
                            </div>
                          )}

                          {sortedInputs
                            .filter(
                              ([key]) =>
                                key !== "review_post_title" &&
                                key !== "review_type"
                            )
                            .map(([key, inputField]) => (
                              <div key={key} className="input-field-block">
                                <label className="input-label">
                                  {inputField.label}* (
                                  {formik.values.inputs[key]?.value?.length ||
                                    0}
                                  /{inputField.characters_limit || 100})
                                </label>
                                <textarea
                                  className="input-textarea"
                                  placeholder={`${inputField.label}...`}
                                  maxLength={inputField.characters_limit || 100}
                                  name={`inputs.${key}.value`}
                                  value={formik.values.inputs[key]?.value || ""}
                                  ref={(el) => autoResize(el)}
                                  onInput={(e) => {
                                    e.currentTarget.style.height = "auto";
                                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                  }}
                                  onChange={(e) =>
                                    handleInputChange(key, e.target.value)
                                  }
                                />
                              </div>
                            ))}

                          {/* <div className="input-field-block">
                        <label className="input-label">Review On*</label>
                        {reviewPost && (
                          <FormControl
                            fullWidth
                            className="styled-select"
                            variant="outlined"
                          >
                            <Select
                              name="review_on"
                              value={formik.values.review_on}
                              onChange={formik.handleChange}
                              IconComponent={KeyboardArrowDownIcon}
                            >
                              <MenuItem value="google">Google</MenuItem>
                              <MenuItem value="facebook">Facebook</MenuItem>
                              <MenuItem value="rate_my_agent">
                                Rate My Agent
                              </MenuItem>
                              <MenuItem value="realestate_com_on">
                                realestate.com.au
                              </MenuItem>
                              <MenuItem value="domain_com_au">
                                domain.com.au
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </div> */}

                          <div className="input-field-block">
                            <label className="input-label">Review On</label>
                            <select
                              name="review_on"
                              className="styled-select"
                              value={formik.values.review_on || ""}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "review_on",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select Review On</option>
                              <option value="google">Google</option>
                              <option value="facebook">Facebook</option>
                              <option value="rate_my_agent">
                                Rate My Agent
                              </option>
                              <option value="realestate_com_on">
                                realestate.com.au
                              </option>
                              <option value="domain_com_au">
                                domain.com.au
                              </option>
                            </select>
                          </div>

                          <div className="action-text-buttons">
                            <span className="text-button" onClick={resetInputs}>
                              Reset Inputs
                            </span>
                            <span
                              className="text-button"
                              onClick={handleUpdatedesign}
                            >
                              Update & Generate New Design
                            </span>
                          </div>
                        </div>

                        <div className="template-preview">
                          <div className="section-heading">Post Preview</div>
                          <div className="template-preview-label">
                            <span>Preview of design template</span>
                            <div className="preview-actions">
                              <span
                                className="preview-link"
                                onClick={() => setPreviewOpen(true)}
                              >
                                Preview
                              </span>
                            </div>
                          </div>
                          <div
                            className={`activity-image-section ${getPreviewContainerClass(
                              postTypeFromUrl
                            )} ${isGenerating ? "loading" : ""}`}
                          >
                            {isGenerating ? (
                              <div className="post-preview-loading">
                                <div className="spinner" />
                                <h4>Your request is being processed.</h4>
                                <p>
                                  We’re working on your request! Creating a
                                  high-quality output can take a little while.
                                  We appreciate your patience.
                                </p>
                                <p className="message">
                                  Feel free to wait here, or browse other pages.
                                  We’ll send you a notification when your
                                  request is completed.
                                </p>
                              </div>
                            ) : (
                              <img
                                src={
                                  templateData?.generatedImage ||
                                  (templateData?.data?.template_image_ref
                                    ?.original_file_path
                                    ? `${API_BASE_URL}/${templateData.data.template_image_ref.original_file_path}`
                                    : templateData?.data?.template_image
                                    ? `${API_BASE_URL}/${templateData.data.template_image}`
                                    : "/images/image-video-generation-error-fallback-image-for-card.png")
                                }
                                alt="Template Preview"
                                className="activity-thumbnail"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    "/images/image-video-generation-error-fallback-image-for-card.png";
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="review-post-form">
                        <h3 className="section-title">Schedule Date & Time</h3>
                        <p>Select date and time to schedule post</p>

                        {localPostStatus === "scheduled" && date && timeTo && (
                          <div className="status-message scheduled">
                            <p>Scheduled for Automatic Posting.</p>
                            <p className="auto-post">
                              Will be published at{" "}
                              {dayjs(timeTo, "HH:mm").format("hh:mm A")},{" "}
                              {dayjs(date).format("DD-MM-YYYY")}.
                            </p>
                          </div>
                        )}

                        {localPostStatus === "published" && date && timeTo && (
                          <div className="status-message published">
                            <p className="auto-post">
                              Post is Completed and already published.
                            </p>
                            <p className="publish-message">
                              Published on{" "}
                              {dayjs(timeTo, "HH:mm").format("hh:mm A")},{" "}
                              {dayjs(date).format("DD-MM-YYYY")}.
                            </p>
                          </div>
                        )}

                        {localPostStatus !== "published" && (
                          <div className="button-group">
                            <button
                              className={`confirm-btn ${
                                selectedButton === "postNow" ? "selected" : ""
                              }`}
                              onClick={() => {
                                if (!date || !timeTo) {
                                  toast.error(
                                    "Please select both date and time to publish."
                                  );
                                  return;
                                }
                                setLocalPostStatus("published");
                                setSelectedButton("postNow");
                                setShowScheduleForm(false);
                              }}
                            >
                              Post Now
                            </button>

                            <button
                              className={`schedule-btn ${
                                showScheduleForm ||
                                selectedButton === "schedule"
                                  ? "active selected"
                                  : ""
                              }`}
                              onClick={() => {
                                setShowScheduleForm(!showScheduleForm);
                                setSelectedButton("schedule");
                              }}
                            >
                              Schedule Post
                            </button>
                          </div>
                        )}

                        {showScheduleForm && (
                          <div className="datetime-row">
                            <div className="datetime-field">
                              <label>Date*</label>
                              <div className="input-icon-wrapper">
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    value={date ? dayjs(date) : null}
                                    onChange={(newValue) =>
                                      setDate(
                                        newValue
                                          ? newValue.format("YYYY-MM-DD")
                                          : ""
                                      )
                                    }
                                    format="DD/MM/YYYY"
                                    slots={{ textField: TextField }}
                                    slotProps={{
                                      textField: {
                                        variant: "outlined",
                                        size: "small",
                                        fullWidth: false,
                                        InputProps: {
                                          className: "input",
                                        },
                                        sx: {
                                          input: {
                                            color: "#000",
                                            "&::placeholder": {
                                              color: "#333",
                                              opacity: 1,
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>
                            </div>

                            <div className="datetime-field">
                              <label>Time*</label>
                              <div className="input-icon-wrapper">
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <TimePicker
                                    value={
                                      timeTo ? dayjs(timeTo, "HH:mm") : null
                                    }
                                    onChange={(newValue) =>
                                      setTimeTo(
                                        newValue ? newValue.format("HH:mm") : ""
                                      )
                                    }
                                    ampm
                                    format="hh:mm A"
                                    slots={{ textField: TextField }}
                                    slotProps={{
                                      textField: {
                                        variant: "outlined",
                                        size: "small",
                                        fullWidth: false,
                                        InputProps: {
                                          className: "input",
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="footer-divider" />
                      <div className="footer-buttons">
                        <button
                          className="btn btn-secondary-changes"
                          type="button"
                          onClick={() => navigate(-1)}
                        >
                          Discard Changes
                        </button>
                        <button
                          className="btn btn-primary"
                          type="submit"
                          disabled={state.isLoading}
                        >
                          {state.isLoading ? (
                            <CircularProgress
                              size={24}
                              style={{ color: "white" }}
                            />
                          ) : hasInputChanges ? (
                            "Update Design"
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </>
          )}

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setProcessingModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>

          <PreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={previewImages}
          />

          {/* <PreviewReviewTemplate
            previewData={reviewPreviewData}
            setPreviewData={setReviewPreviewData}
          /> */}

          {activeTab === "post_assets" && <PostAssets />}

          {activeTab === "postConfig" && <ReviewPostConfiguration />}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default ReviewPostView;
